'use client';

import { useEffect, useRef, useState } from 'react';
import { UNSAFE_PortalProvider } from '@clerk/nextjs';
import { Dialog, DialogPortal, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AuthenticationForm } from '@/components/auth/AuthenticationForm';

interface ClaimProfileDialogProps {
  claimUrl: string;
  companyName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  restoreFocus: () => void;
}

export const ClaimProfileDialog = ({ claimUrl, companyName, open, onOpenChange, restoreFocus }: ClaimProfileDialogProps) => {
  const [busy, setBusy] = useState(false);
  const content = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [open]);

  // Clerk can open its own verification challenge above this dialog. Allow its
  // portal to receive focus, while retaining the overlay and keyboard focus loop.
  return <Dialog modal={false} open={open} onOpenChange={(nextOpen) => { if (!busy) { onOpenChange(nextOpen); } }}>
    {open && <DialogPortal><div aria-hidden="true" className="fixed inset-0 z-50 bg-black/60" onClick={() => { if (!busy) { onOpenChange(false); } }} /></DialogPortal>}
    {open && <DialogContent ref={content} aria-modal="true" onInteractOutside={(event) => { event.preventDefault(); }} onCloseAutoFocus={(event) => { event.preventDefault(); restoreFocus(); }} className="max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border-gray-200/80 bg-white p-6 shadow-xl sm:max-w-[30rem] sm:rounded-xl sm:p-8">
      <UNSAFE_PortalProvider getContainer={() => { return content.current; }}>
        <DialogTitle className="sr-only"><span>{`Claim ${companyName}`}</span></DialogTitle>
        <DialogDescription className="sr-only"><span>Sign in or create an account to continue your company claim.</span></DialogDescription>
        <AuthenticationForm mode="claim" destination={claimUrl} companyName={companyName} onBusyChange={setBusy} />
      </UNSAFE_PortalProvider>
    </DialogContent>}
  </Dialog>;
};
